/** @jsxImportSource @emotion/react */
import type { ApiScan } from '@kaiizen/kai-pro-api';
import {
  ScanEditingContextProvider,
  useScanEditingContextSlice,
  useScopeReportData,
} from '@kaiizen/kai-pro-api-react-state';
import { formatCurrency } from '@kaiizen/kai-reports';
import { getTotalCostIfDefined } from '@kaiizen/kai-scan-template-system';

import { StickyNoteIcon } from '@/Client/Widgets/StyledIcons/DocumentIcon';
import { ArtifactEmptyState } from './ArtifactEmptyState';
import { ArtifactSelectableCard } from './ArtifactSelectableCard';
import {
  formatArtifactAmount,
  getHistoricalArtifactBadge,
  getHistoricalArtifactBadgeTone,
  getHistoricalArtifactSubtitle,
  getScopeArtifactDocuments,
  type HistoricalArtifactDocumentType,
} from './FocusedArtifactPanelLib';
import {
  badgeStyle,
  CARD_AMOUNT_ROW_STYLE,
  CARD_AMOUNT_STYLE,
  CARD_DIVIDER_STYLE,
  CARD_KICKER_STYLE,
  CARD_SUBTITLE_STYLE,
  CARD_TITLE_STYLE,
  HISTORY_CARD_STYLE,
  LOADING_TEXT_STYLE,
  SECTION_COPY_STYLE,
  SECTION_DESCRIPTION_STYLE,
  SECTION_STYLE,
  SECTION_TITLE_STYLE,
  THREE_UP_CARD_GRID_STYLE,
} from './FocusedArtifactPanelStyleLib';

/** Renders a change-order card amount through the same snapshot-diff pipeline as the legacy report. */
const ChangeOrderArtifactAmount = () => {
  const { diffReportData, isError, isLoading } = useScopeReportData();
  if (isLoading || isError) return <>—</>;

  const totalDifference = diffReportData?.overviewData.totalDifference ?? 0;
  return <>{totalDifference > 0 ? '+' : ''}{formatCurrency(totalDifference)}</>;
};

/** Renders approved scope history, active drafts, and the final closeout artifact card. */
export const HistoricalArtifactsSection = ({
  closeoutArtifact,
  hasAnyScopeArtifacts,
  latestApprovedScopeArtifactId,
  isLoading,
  scopeArtifacts,
  selectedArtifactId,
  selectedArtifactDocumentType,
  changeOrderNumberById,
  onSelectArtifact,
}: {
  closeoutArtifact?: ApiScan;
  hasAnyScopeArtifacts: boolean;
  latestApprovedScopeArtifactId?: string;
  isLoading: boolean;
  scopeArtifacts: ApiScan[];
  selectedArtifactId?: string;
  selectedArtifactDocumentType?: HistoricalArtifactDocumentType;
  changeOrderNumberById: Map<string, number>;
  onSelectArtifact: (
    artifactScanId: string,
    documentType: HistoricalArtifactDocumentType,
  ) => void;
}) => {
  const { scanId, scanRoot } = useScanEditingContextSlice(['scanId', 'scanRoot']);
  const currentScopeAmount = getTotalCostIfDefined(scanRoot);

  if (isLoading) {
    return (
      <section css={SECTION_STYLE}>
        <div css={SECTION_COPY_STYLE}>
          <div css={SECTION_TITLE_STYLE}>Historical artifacts</div>
          <div css={SECTION_DESCRIPTION_STYLE}>
            Review the original scope, each change-order version, and the final closeout document.
          </div>
        </div>
        <div css={LOADING_TEXT_STYLE}>Loading artifacts...</div>
      </section>
    );
  }

  if (scopeArtifacts.length === 0 && !closeoutArtifact) {
    return (
      <section css={SECTION_STYLE}>
        <ArtifactEmptyState
          variant='canvas'
          icon={<StickyNoteIcon width={24} height={24} color='currentColor' aria-hidden='true' />}
          title={hasAnyScopeArtifacts ? 'No active scope version yet' : 'No scope submitted yet'}
          description={hasAnyScopeArtifacts
            ? 'No active scope version is available. Rejected scope versions are not shown as artifacts.'
            : 'Finish building the scope and submit it for review. The approved artifact with line items, prices, and totals will live here.'}
          detail={hasAnyScopeArtifacts
            ? 'Approved and in-progress scope versions populate this audit trail'
            : 'Submit the scope for review from the top-right button'}
        />
      </section>
    );
  }

  return (
    <section css={SECTION_STYLE}>
      <div css={SECTION_COPY_STYLE}>
        <div css={SECTION_TITLE_STYLE}>Historical artifacts</div>
        <div css={SECTION_DESCRIPTION_STYLE}>
          Review approved scope history, active draft versions, and the final closeout document.
        </div>
      </div>
      <div css={THREE_UP_CARD_GRID_STYLE}>
        {scopeArtifacts.flatMap(scopeArtifact => {
          const badge = getHistoricalArtifactBadge({
            scan: scopeArtifact,
            latestApprovedArtifactId: latestApprovedScopeArtifactId,
          });

          return getScopeArtifactDocuments(scopeArtifact, changeOrderNumberById).map(artifactDocument => (
            <ArtifactSelectableCard
              key={`${scopeArtifact.id}-${artifactDocument.documentType}`}
              selected={scopeArtifact.id === selectedArtifactId
                && artifactDocument.documentType === selectedArtifactDocumentType}
              extraCss={HISTORY_CARD_STYLE}
              title={`View ${artifactDocument.title}`}
              onSelect={() => onSelectArtifact(scopeArtifact.id, artifactDocument.documentType)}
            >
              <div css={CARD_KICKER_STYLE}>{artifactDocument.kicker}</div>
              <div css={CARD_TITLE_STYLE}>
                {artifactDocument.title}
              </div>
              <div css={CARD_SUBTITLE_STYLE}>
                {getHistoricalArtifactSubtitle(scopeArtifact)}
              </div>
              <div css={CARD_DIVIDER_STYLE} />
              <div css={CARD_AMOUNT_ROW_STYLE}>
                <div css={CARD_AMOUNT_STYLE}>
                  {artifactDocument.documentType === 'changeOrder'
                    ? (
                      <ScanEditingContextProvider
                        scanId={scopeArtifact.id}
                        userHasAdminScanAccess
                      >
                        <ChangeOrderArtifactAmount />
                      </ScanEditingContextProvider>
                    )
                    : formatArtifactAmount(
                      scopeArtifact,
                      scopeArtifact.id === scanId ? currentScopeAmount : undefined,
                    )}
                </div>
                <div css={badgeStyle(getHistoricalArtifactBadgeTone(badge))}>
                  {badge}
                </div>
              </div>
            </ArtifactSelectableCard>
          ));
        })}
        {closeoutArtifact && (
          <ArtifactSelectableCard
            selected={closeoutArtifact.id === selectedArtifactId && selectedArtifactDocumentType === 'closeout'}
            extraCss={HISTORY_CARD_STYLE}
            title='View Closeout'
            onSelect={() => onSelectArtifact(closeoutArtifact.id, 'closeout')}
          >
            <div css={CARD_KICKER_STYLE}>Closeout document</div>
            <div css={CARD_TITLE_STYLE}>Closeout</div>
            <div css={CARD_SUBTITLE_STYLE}>{getHistoricalArtifactSubtitle(closeoutArtifact)}</div>
            <div css={CARD_DIVIDER_STYLE} />
            <div css={CARD_AMOUNT_ROW_STYLE}>
              <div css={CARD_AMOUNT_STYLE}>{formatArtifactAmount(closeoutArtifact)}</div>
              <div css={badgeStyle('neutral')}>Approved</div>
            </div>
          </ArtifactSelectableCard>
        )}
      </div>
    </section>
  );
};
